/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * kCoreObjects — server-safe base classes for typed data objects and collections.
 *
 * Exports:
 *   kObjectCore      — base data object with JSON serialisation and deep-clone
 *   kObjectArrayCore — typed collection with sort, lookup, and async iteration
 *
 * No browser APIs. Safe to import in Node.js, Azure Functions, or the browser.
 */

import {
    getCoreUtils, nz, nb, n0, kNotNullOrUndefined, kNotEmptyString, getRandomInt, doCallBack, kCore,
} from "./kCoreHandler";
import { kTimeCore } from "./kCoreTime";

// ── kObjectCore ───────────────────────────────────────────────────────────────

/**
 * Base class for all typed data objects.
 *
 * Provides:
 *   - Standard identity fields: Title, ID, Index, UniqueID
 *   - ToJSONObj / FromSaved for round-trip JSON serialisation
 *   - CloneObject for deep-copying into a subclass
 */
export class kObjectCore {
    public Title = "";
    public ID = 0;
    public Index = 0;
    public UniqueID = 0;

    // Transient per-session state that must NEVER reach persisted JSON, skipped by
    // ToJSONObj below. Both belong to the search layer (kSearchItemCore): SearchableString
    // is derived - its setter recomputes the accent-folded copy whenever it is assigned -
    // and FilteredOut is a live filter flag, so persisting them bloats every saved payload
    // and can restore a stale "hidden" state on the next load. The list lives here rather
    // than as a ToJSONObj override in kSearchItemCore because the house style declares
    // methods as arrow PROPERTIES, and an arrow property cannot chain to the base version
    // via super - overriding would mean copying the whole method.
    // SearchFields and _ExtraSearchText belong to kSearchItemCore, which extends this class -
    // listed here because ToJSONObj consults this one static and never the subclass's. They
    // are search WIRING, not data: persisting them would write a field-name list into every
    // saved object and, worse, FromSaved would restore a stale one over a newer declaration.
    public static TransientProperties: string[] = [
        "_SearchableString", "FilteredOut", "SearchFields", "_ExtraSearchText",
    ];

    /**
     * The restore counterpart of TransientProperties: read a saved object's hidden flag as a
     * real BOOLEAN, whether or not the key survived serialisation.
     *
     * WHY THIS EXISTS. Because FilteredOut is transient, an object written by ToJSONObj has
     * NO `FilteredOut` key at all - and older payloads spell it `Hide`. A consumer whose
     * FromSaved does the obvious `this.FilteredOut = Saved.FilteredOut` therefore writes
     * `undefined` OVER the `false` default, and every `FilteredOut === false` test in that
     * product then answers false while looking perfectly correct.
     *
     * Runtime-confirmed 2026-08-10 in Kanban Task Manager: three classes restored it that
     * way, so a board whose settings had been re-saved since FilteredOut became transient
     * drew its column headers and then ZERO cards - `KanbanLane` builds one cell per phase
     * with `FilteredOut === false` and got none. No error, no console message; the product
     * just rendered an empty board. Any product with the same FromSaved shape has the same
     * latent bug, which is why the helper lives beside the list that causes it.
     *
     * Absent means NOT hidden - the safe default, since a hidden item is invisible.
     */
    public static HiddenFromSaved = (SavedObject: any): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            if (SavedObject === null || SavedObject === undefined) { return false; }
            //`Hide` is the legacy spelling and wins when present, exactly as the hand-rolled
            //versions this replaces did.
            if (SavedObject.Hide !== undefined && SavedObject.Hide !== null) {
                return SavedObject.Hide === true;
            }
            return SavedObject.FilteredOut === true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kObjectCore.HiddenFromSaved");
            return false;
        }
    };

    // Stub methods — browser kObject overrides these with real DOM implementations.
    // Present here so the type system treats kObjectCore and kObject as compatible.
    public ShowProgress = (ProgressMessage: string, Done: number, Total: number): void => { };
    public ToRow = (): any => { return null; };
    public FromRow = (CurrRow: any): void => { };
    public ToAnchor = (): any => { return null; };

    // ── Search support ────────────────────────────────────────────────────────
    // Real implementations (not stubs) and DOM-free, so they belong here rather than in a
    // separate searchable subclass. Two reasons they sit on the BASE object:
    //   1. kObjectArrayCore.Items is kObjectCore[], and the search engine in
    //      kSearchCore.ts iterates exactly that - with CheckMatch here it needs no cast.
    //   2. The stubs above exist so kObjectCore stays assignable to the browser kObject.
    //      Hanging search off a subclass instead breaks that contract the moment kObject
    //      inherits it (verified 2026-07-30: kHTML, kEditableTable and KTMTask all failed
    //      with "Type 'kObjectCore' is missing ... CheckMatch").
    // Neither field is persisted - see TransientProperties above.
    private _SearchableString: string = "";
    public get SearchableString(): string { return this._SearchableString; }
    // Stored accent-folded so matching is accent-insensitive (see kCore.FoldAccents).
    // CheckMatch folds the incoming term the same way, so both sides are comparable.
    public set SearchableString(Value: string) { this._SearchableString = kCore.FoldAccents(Value); }
    public FilteredOut: boolean = false;

    public CheckMatch = (SearchTerm: string): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            if (this.FilteredOut === false) {
                const Folded: string = kCore.FoldAccents(SearchTerm);
                if (this.SearchableString.indexOf(Folded) === -1) {
                    this.FilteredOut = true;
                }
            }
            return (this.FilteredOut === false);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kObjectCore.CheckMatch");
            return false;
        }
    };

    public constructor(Title?: string) {
        this.Title = nz(Title);
        this.UniqueID = new Date().getTime() + Math.floor(Math.random() * 1000000);
    }

    /**
     * Serialise to a plain, JSON-safe object.
     * kTime fields are stored as ISO strings ("yyyy-MM-dd HH:mm").
     * Functions and complex objects are omitted.
     */
    public ToJSONObj = (): any => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            const Saved: any = {};
            const Self = this as any;
            for (const Property in this) {
                if (typeof Self[Property] === "function") { continue; }
                if (kObjectCore.TransientProperties.indexOf(Property) !== -1) { continue; }
                let PropConstructorName: string = "";
                if (Self[Property] !== null && Self[Property] !== undefined) {
                    if (Self[Property].constructor !== null && Self[Property].constructor !== undefined) {
                        PropConstructorName = Self[Property].constructor.name;
                    }
                }
                if (typeof Self[Property] === "object"
                    && PropConstructorName === "kTime") {
                    Saved[Property] = (Self[Property] as kTimeCore).ToISODateTime();
                } else if (typeof Self[Property] !== "object") {
                    Saved[Property] = Self[Property];
                }
            }
            return Saved;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kObjectCore.ToJSONObj"); return; }
    };

    /**
     * Restore fields from a plain object previously produced by ToJSONObj.
     * kTime fields are detected by constructor name and parsed from their ISO string.
     */
    public FromSaved = (JSONObj: any): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            const Self = this as any;
            for (const Property in JSONObj) {
                let ClassName = "";
                try {
                    if (Self[Property] !== null && Self[Property] !== undefined && Self[Property].constructor) {
                        ClassName = Self[Property].constructor.name;
                    }
                } catch (e) { /* field may not exist yet on this instance */ }
                if (ClassName === "kTime") {
                    (Self[Property] as kTimeCore).FromISODateTime(nz(JSONObj[Property]));
                } else {
                    Self[Property] = JSONObj[Property];
                }
            }
            // backwards compatibility: older saved objects used "Name" instead of "Title"
            if (this.Title === undefined) { this.Title = JSONObj.Name; }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kObjectCore.FromSaved"); }
    };

    /**
     * Deep-clone this instance into a new object of type NewClass.
     * NewClass must extend kObjectCore and accept an optional title string in its constructor.
     */
    public CloneObject = (NewClass: any): any => {
        if (getCoreUtils().KErrorMode) { return null; }
        try {
            const NewObj: kObjectCore = new NewClass(this.Title);
            NewObj.FromSaved(this.ToJSONObj());
            return NewObj;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kObjectCore.CloneObject"); return null; }
    };
}

// ── kObjectArrayCore ─────────────────────────────────────────────────────────

/**
 * Typed collection of kObjectCore instances.
 *
 * Provides:
 *   - CRUD: push, Remove*, GetItemBy*
 *   - Sort: SortByTitle, SortByProperty, SortAndRemoveDoubles
 *   - Serialisation: ToPersistedArray / FromPersistedArray, ToTitlesArray / FromTitlesArray
 *   - Async iteration: CallFunctionOnEach (small setTimeout between calls to prevent stack overflow)
 */
export class kObjectArrayCore {
    public Items: kObjectCore[] = [];
    /** Constructor used by GetOrAddByTitle, FromPersistedArray, and FromTitlesArray. */
    public NewItem: new (title?: string) => kObjectCore = kObjectCore;
    // eslint-disable-next-line @typescript-eslint/ban-types
    public ExtendedClass: Function | null = null;

    protected _ActionCounter = 0;
    protected _FunctionName: string = "";
    protected _ObjectName: string = "";
    // eslint-disable-next-line @typescript-eslint/ban-types
    protected _ActionDoneCallback: Function | null = null;
    protected _ProgressMessage = "";
    protected _ProgressCB: any = null;
    protected _ForEachPar: any = null;
    protected _SortPropName = "";
    protected _SortPropNumeric = false;

    // ── Collection management ─────────────────────────────────────────────────

    /** Append an item and update its Index field. */
    public push = (NewObject: kObjectCore): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            if (kNotNullOrUndefined(NewObject)) {
                NewObject.Index = this.Items.length;
                this.Items.push(NewObject);
            }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kObjectArrayCore.push"); }
    };

    /** Remove the item at the given array index. */
    public RemoveItemByIndex = (Index: number): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            this.Items.splice(Index, 1);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kObjectArrayCore.RemoveItemByIndex"); }
    };

    // The remove-by-lookup spine shared by RemoveItemByTitle, RemoveItemByUniqueID and
    // RemoveItemByID (finding 7746): find the item, remove it by its current index when
    // found.
    // DOCUMENTED EXCEPTION: pure lookup+remove under the caller's own gate+try and
    // per-member label.
    private _RemoveFirstMatching = (Find: () => kObjectCore): void => {
        const Item: kObjectCore = Find();
        if (kNotNullOrUndefined(Item)) { this.RemoveItemByIndex(Item.Index); }
    };

    /** Remove the first item whose Title matches. */
    public RemoveItemByTitle = (Title: string): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            this._RemoveFirstMatching((): kObjectCore => this.GetItemByTitle(Title));
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kObjectArrayCore.RemoveItemByTitle"); }
    };

    /** Remove the item with the given UniqueID. */
    public RemoveItemByUniqueID = (UniqueID: number): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            this._RemoveFirstMatching((): kObjectCore => this.GetItemByUniqueID(UniqueID));
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kObjectArrayCore.RemoveItemByUniqueID"); }
    };

    /** Remove the item with the given ID. */
    public RemoveItemByID = (ID: number): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            this._RemoveFirstMatching((): kObjectCore => this.GetItemByID(ID));
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kObjectArrayCore.RemoveItemByID"); }
    };

    // ── Lookup ────────────────────────────────────────────────────────────────

    /** Find by Title. Updates Index on all items. Returns null when not found. */
    public GetItemByTitle = (Title: string): any => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            let Found: kObjectCore | null = null;
            let Index = 0;
            for (const Item of this.Items) {
                if (Item.Title === Title) { Found = Item; }
                Item.Index = Index++;
            }
            return Found;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kObjectArrayCore.GetItemByTitle"); }
    };

    /**
     * Find by Title, ignoring case. Returns null when not found.
     *
     * Exists because titles legitimately drift in case between what a caller asks for and what the
     * source holds: SharePoint list creation POSTs a lowercase URL-safe title and renames afterwards,
     * a MERGE rename has an eventual-consistency window, and locale folding differs per site. Callers
     * that must survive that window used to hand-roll this scan (kSPRest.GetOrCreatekSPListByTitle
     * had its own copy); lifted here on the second consumer.
     *
     * Deliberately NOT folded into GetItemByTitle: an exact match is the right default for anything
     * keyed by title, and quietly making it case-blind would change lookups across every consumer of
     * every collection in the estate.
     */
    public GetItemByTitleCI = (Title: string): any => {
        if (getCoreUtils().KErrorMode) { return null; }
        try {
            const Wanted: string = nz(Title).toLowerCase();
            let Item: kObjectCore;
            for (Item of this.Items) {
                if (nz(Item.Title).toLowerCase() === Wanted) { return Item; }
            }
            return null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kObjectArrayCore.GetItemByTitleCI");
            return null;
        }
    };

    /** Return the item with the given Title, or create and push a new one. */
    public GetOrAddByTitle = (Title: string): kObjectCore => {
        if (getCoreUtils().KErrorMode) { return new kObjectCore(); }
        try {
            const Existing: kObjectCore | null = this.GetItemByTitle(Title);
            if (Existing === null) {
                const New: kObjectCore = new this.NewItem();
                New.Title = Title;
                this.push(New);
                return New;
            }
            return Existing;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kObjectArrayCore.GetOrAddByTitle");
            return new kObjectCore();
        }
    };

    /** Find by UniqueID. Updates Index on all items. */
    public GetItemByUniqueID = (UniqueID: number): any => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            let Found: kObjectCore | null = null;
            let Index = 0;
            for (const Item of this.Items) {
                if (Item.UniqueID === UniqueID) { Found = Item; }
                Item.Index = Index++;
            }
            return Found;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kObjectArrayCore.GetItemByUniqueID"); }
    };

    /** First item whose named property strictly equals Value, or null. Does not renumber Index. */
    public GetItemByProperty = (Property: string, Value: unknown): any => {
        if (getCoreUtils().KErrorMode) { return null; }
        try {
            for (const Item of this.Items) {
                if ((Item as any)[Property] === Value) { return Item; }
            }
            return null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kObjectArrayCore.GetItemByProperty");
            return null;
        }
    };

    /** Find by ID. Updates Index on all items. */
    public GetItemByID = (ID: number): any => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            let Found: kObjectCore | null = null;
            let Index = 0;
            for (const Item of this.Items) {
                if (Item.ID === ID) { Found = Item; }
                Item.Index = Index++;
            }
            return Found;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kObjectArrayCore.GetItemByID"); }
    };

    /** Return a random item from the collection. */
    public GetRandomItem = (): kObjectCore => {
        if (getCoreUtils().KErrorMode) { return new kObjectCore(); }
        try {
            return this.Items[getRandomInt(0, this.Items.length - 1)];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kObjectArrayCore.GetRandomItem");
            return new kObjectCore();
        }
    };

    // ── Sorting ───────────────────────────────────────────────────────────────

    /**
     * Sort items by Title, case-insensitively.
     * @param Natural  false (default) = the original character order, unchanged for every existing caller;
     *                 true = natural order through kCore.NaturalCompare (digit runs as numbers: "a 9" before "a 10").
     */
    public SortByTitle = (Natural: boolean = false): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            if (Natural === true) { this.Items.sort(this._SortByTitleNaturalFn); } else { this.Items.sort(this._SortByTitleFn); }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kObjectArrayCore.SortByTitle"); }
    };

    /** DOCUMENTED EXCEPTION: delegate - kCore.NaturalCompare carries the guard. */
    protected _SortByTitleNaturalFn = (A: kObjectCore, B: kObjectCore): number => {
        return kCore.NaturalCompare(A.Title, B.Title);
    };

    protected _SortByTitleFn = (A: kObjectCore, B: kObjectCore): number => {
        if (A.Title.toLowerCase() < B.Title.toLowerCase()) { return -1; }
        if (A.Title.toLowerCase() > B.Title.toLowerCase()) { return 1; }
        return 0;
    };

    /**
     * Sort by any property.
     * @param Numeric  When true, values are compared numerically rather than as strings.
     */
    public SortByProperty = (Property: string, Ascending = true, Numeric?: boolean): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            this._SortPropName = Property;
            this._SortPropNumeric = nb(Numeric);
            this.Items.sort(Ascending ? this._SortPropAscFn : this._SortPropDescFn);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kObjectArrayCore.SortByProperty"); }
    };

    protected _SortPropAscFn = (A: any, B: any): number => {
        if (!kNotNullOrUndefined(A[this._SortPropName])
            || !kNotNullOrUndefined(B[this._SortPropName])) {
            return 0;
        }
        if (this._SortPropNumeric) {
            return n0(A[this._SortPropName]) - n0(B[this._SortPropName]);
        }
        const AVal: string = nz(A[this._SortPropName]);
        const BVal: string = nz(B[this._SortPropName]);
        return AVal < BVal ? -1 : AVal > BVal ? 1 : 0;
    };

    protected _SortPropDescFn = (A: any, B: any): number => -this._SortPropAscFn(A, B);

    /** Sort by Title then remove items with duplicate titles. */
    public SortAndRemoveDoubles = (): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            this.SortByTitle();
            this.RemoveTitleDoubles();
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kObjectArrayCore.SortAndRemoveDoubles"); }
    };

    /** Remove items whose Title already appeared earlier in the array. */
    public RemoveTitleDoubles = (): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            const Seen: string[] = [];
            const Unique: kObjectCore[] = [];
            for (const Item of this.Items) {
                if (kNotNullOrUndefined(Item.Title) && Seen.indexOf(nz(Item.Title)) === -1) {
                    Item.Index = Unique.length;
                    Unique.push(Item);
                    Seen.push(nz(Item.Title));
                }
            }
            this.Items = Unique;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kObjectArrayCore.RemoveTitleDoubles"); }
    };

    // ── Serialisation ─────────────────────────────────────────────────────────

    /** Return an array of Title strings, optionally sorted. */
    public ToTitlesArray = (Sort?: boolean): string[] => {
        if (getCoreUtils().KErrorMode) { return []; }
        try {
            const Titles: string[] = [];
            let Index = 0;
            for (const Item of this.Items) {
                Titles.push(nz(Item.Title));
                Item.Index = Index++;
            }
            return Sort === true ? Titles.sort() : Titles;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kObjectArrayCore.ToTitlesArray"); return []; }
    };

    /** Serialise all items to plain JSON-safe objects (via kObject.ToJSONObj). */
    public ToPersistedArray = (): any[] => {
        if (getCoreUtils().KErrorMode) { return []; }
        try {
            return this.Items.map(kObjectArrayCore._ToJSONObjOf);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kObjectArrayCore.ToPersistedArray"); return []; }
    };

    /**
     * DOCUMENTED EXCEPTION: pure one-line projection used as the named `map` callback for
     * ToPersistedArray. It delegates straight to ToJSONObj, which carries its own guard and
     * try/catch, so a second pair here would only hide which item failed.
     */
    private static _ToJSONObjOf = (Item: kObjectCore): any => { return Item.ToJSONObj(); };

    /** Restore items from a plain array previously produced by ToPersistedArray. */
    public FromPersistedArray = (PersistedArray: any[]): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            this.Items = [];
            if (kNotNullOrUndefined(PersistedArray) && PersistedArray.length > 0) {
                for (const Obj of PersistedArray) {
                    const Item: kObjectCore = new this.NewItem();
                    Item.FromSaved(Obj);
                    this.push(Item);
                }
            }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kObjectArrayCore.FromPersistedArray"); }
    };

    /** Create an item for each string in Titles. Pass Empty=true to clear first. */
    public FromTitlesArray = (Titles: string[], Empty?: boolean): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            if (Empty) { this.Items = []; }
            for (const Title of Titles) {
                const New: any = new this.NewItem();
                New.Index = this.Items.length;
                New.Title = Title;
                this.push(New);
            }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kObjectArrayCore.FromTitlesArray"); }
    };

    // ── Async iteration ───────────────────────────────────────────────────────

    /**
     * Call FunctionName on each item sequentially.
     * A 5 ms setTimeout between calls prevents stack overflows on large arrays
     * and keeps the event loop responsive.
     *
     * @param FunctionName   Method name (string) to call on each item.
     * @param AllDone        Callback invoked after the last item.
     * @param ObjectName     Optional sub-object name on each item (calls item[ObjectName][FunctionName]).
     * @param StartIndex     Resume from this index (default 0).
     * @param ProgressMessage  Passed to the ProgressCallback on each step.
     * @param ProgressCallback Optional callback(message, done, total) for progress reporting.
     * @param Parameter      Forwarded as the first argument to each method call.
     */
    public CallFunctionOnEach = (
        FunctionName: any,
        AllDone: any,
        ObjectName?: any,
        StartIndex?: number,
        ProgressMessage?: string,
        ProgressCallback?: any,
        Parameter?: any
    ): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            this._ObjectName = nz(ObjectName);
            this._FunctionName = FunctionName;
            this._ActionDoneCallback = AllDone;
            this._ForEachPar = Parameter;
            if (ProgressCallback) { this._ProgressCB = ProgressCallback; }
            this._ActionCounter = 0;
            if (kNotNullOrUndefined(StartIndex)) { this._ActionCounter = n0(StartIndex); }
            this._ProgressMessage = nz(ProgressMessage);
            this._NextCallback();
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kObjectArrayCore.CallFunctionOnEach"); }
    };

    protected _NextCallback = (): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            if (this._ActionCounter < this.Items.length) {
                this._ActionCounter++;
                if (this._ProgressCB) {
                    this._ProgressCB(this._ProgressMessage, this._ActionCounter, this.Items.length);
                }
                setTimeout(this._OnInvokeCurrent, 5);
            } else {
                doCallBack(this._ActionDoneCallback);
            }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kObjectArrayCore._NextCallback"); }
    };

    /**
     * Calls the configured method on the item the counter is currently pointing at.
     *
     * A named class property rather than the inline closure this used to be. The item is
     * RE-READ from `_ActionCounter` at fire time instead of being captured: the counter
     * cannot move in between, because the only thing that advances it is _NextCallback,
     * and that runs only once the item's own callback fires - i.e. strictly after this.
     * Re-reading also means the chain walks the CURRENT Items array rather than holding a
     * reference to an element of a possibly-replaced one.
     */
    protected _OnInvokeCurrent = (): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            const CurrentItem: any = this.Items[this._ActionCounter - 1] as any;
            if (kNotEmptyString(this._ObjectName)) {
                CurrentItem[this._ObjectName][this._FunctionName](this._ForEachPar, this._NextCallback);
            } else {
                CurrentItem[this._FunctionName](this._ForEachPar, this._NextCallback);
            }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kObjectArrayCore._OnInvokeCurrent"); }
    };
}
